import { useEffect, useState } from "react";
import { ArrowRight } from "@phosphor-icons/react";
import { useLang } from "@/i18n";

const AUTO_MS = 6000;
const SESSION_KEY = "oomining_intro_seen";

/**
 * Fullscreen intro splash shown before the login form on first visit of
 * the browser session. Auto-dismisses after 8 seconds; users can also
 * skip via the "Ir al login" button. Uses sessionStorage so it only
 * shows once per browser session — not on every re-render.
 */
export default function IntroSplash({ onDismiss }) {
  const { t, lang } = useLang();
  const [progress, setProgress] = useState(0);
  const [remaining, setRemaining] = useState(Math.ceil(AUTO_MS / 1000));
  const [leaving, setLeaving] = useState(false);

  useEffect(() => {
    const started = Date.now();
    const tick = setInterval(() => {
      const elapsed = Date.now() - started;
      const pct = Math.min(100, (elapsed / AUTO_MS) * 100);
      setProgress(pct);
      setRemaining(Math.max(0, Math.ceil((AUTO_MS - elapsed) / 1000)));
      if (elapsed >= AUTO_MS) {
        clearInterval(tick);
        dismiss();
      }
    }, 80);
    return () => clearInterval(tick);
  }, []);

  const dismiss = () => {
    if (leaving) return;
    setLeaving(true);
    try { sessionStorage.setItem(SESSION_KEY, "1"); } catch (_e) { /* noop */ }
    // Fade out ~350ms then let parent unmount
    setTimeout(() => { onDismiss?.(); }, 350);
  };

  return (
    <div
      data-testid="intro-splash"
      className={`fixed inset-0 z-[100000] flex flex-col items-center justify-center bg-white transition-opacity duration-300 ${leaving ? "opacity-0" : "opacity-100"}`}
    >
      {/* Subtle gradient wash for depth */}
      <div className="absolute inset-0 bg-gradient-to-br from-white via-blue-50/40 to-orange-50/30" />

      <div className="relative z-10 flex flex-col items-center max-w-3xl px-6">
        <img
          src="/img/oomining-intro.jpg"
          alt="OOMINING · Mine Management Software"
          data-testid="intro-splash-logo"
          className="w-full max-w-2xl h-auto animate-[fadeInScale_800ms_ease-out]"
          style={{ filter: "drop-shadow(0 6px 24px rgba(30,58,138,0.15))" }}
        />

        <p className="mt-8 text-[11px] uppercase tracking-[0.35em] font-bold text-slate-500 animate-[fadeInUp_1200ms_ease-out_400ms_backwards]">
          {lang === "en" ? "Enterprise mining management" : "Gestión minera empresarial"}
        </p>

        <button
          onClick={dismiss}
          data-testid="intro-splash-skip"
          className="mt-10 group inline-flex items-center gap-2 rounded-full bg-blue-900 hover:bg-blue-800 text-white font-bold text-sm px-6 py-3 shadow-[0_10px_30px_rgba(30,58,138,0.35)] hover:shadow-[0_14px_34px_rgba(30,58,138,0.45)] transition-all active:scale-95 animate-[fadeInUp_1200ms_ease-out_600ms_backwards]"
        >
          {lang === "en" ? "Continue to login" : "Ir al login"}
          <ArrowRight size={16} weight="bold" className="group-hover:translate-x-0.5 transition-transform" />
        </button>

        <p className="mt-4 text-xs text-slate-400 tabular-nums">
          {lang === "en" ? "Continuing automatically in" : "Continuando automáticamente en"} {remaining}s
        </p>
      </div>

      {/* Progress bar bottom */}
      <div className="absolute bottom-0 left-0 right-0 h-1 bg-slate-100">
        <div
          data-testid="intro-splash-progress"
          className="h-full bg-gradient-to-r from-blue-900 to-orange-500 transition-[width] duration-100 ease-linear"
          style={{ width: `${progress}%` }}
        />
      </div>

      <style>{`
        @keyframes fadeInScale {
          from { opacity: 0; transform: scale(0.94); }
          to { opacity: 1; transform: scale(1); }
        }
        @keyframes fadeInUp {
          from { opacity: 0; transform: translateY(12px); }
          to { opacity: 1; transform: translateY(0); }
        }
      `}</style>
    </div>
  );
}

export function shouldShowIntro() {
  try {
    return !sessionStorage.getItem(SESSION_KEY);
  } catch (_e) {
    return true;
  }
}
